import { Button } from "@/lib/components/Button/Button";
import { Input } from "@/lib/components/Input";
import { RadioGroup, RadioGroupItem } from "@/lib/components/RadioGroup";
import { Label } from "@/lib/components/Label";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useState, useMemo, useCallback } from "react";
import toasts from "@/lib/components/Toast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { IconInfoCircle } from "@tabler/icons-react";

function UpdateVoiceActor() {
  const {
    voiceActorExternalId,
    isGlobal,
    setVoiceActorExternalId,
    setIsGlobal,
    isValid,
    handleSubmit,
    reset,
    isLoading,
  } = useUpdateVoiceActorForm();

  return (
    <form
      className="flex h-full flex-col overflow-hidden py-6"
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="flex flex-1 flex-col gap-4">
        <div className="flex flex-col">
          <div className="input-label">Voice Actor External ID</div>
          <Input
            value={voiceActorExternalId}
            onChange={(e) => setVoiceActorExternalId(e.target.value)}
            placeholder="Enter voice actor external ID"
          />
        </div>
        <div className="flex flex-col">
          <div className="input-label">Scope</div>
          <RadioGroup
            value={isGlobal ? "global" : "team"}
            onValueChange={(value) => setIsGlobal(value === "global")}
            className="ml-0.25 flex flex-col gap-3"
          >
            <div className="flex items-center gap-3">
              <RadioGroupItem value="team" id="update-scope-team" />
              <Label
                htmlFor="update-scope-team"
                className="flex items-center gap-1"
              >
                Update for current team only
                <Tooltip>
                  <TooltipTrigger>
                    <IconInfoCircle className="inline-block" size={16} />
                  </TooltipTrigger>
                  <TooltipContent>
                    Cannot be updated to this if already set to global
                  </TooltipContent>
                </Tooltip>
              </Label>
            </div>
            <div className="flex items-center gap-3">
              <RadioGroupItem value="global" id="update-scope-global" />
              <Label htmlFor="update-scope-global">
                Update globally for all teams
              </Label>
            </div>
          </RadioGroup>
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <Button
          onClick={handleSubmit}
          disabled={!isValid || isLoading}
          isLoading={isLoading}
        >
          Update voice actor
        </Button>
        <Button variant="outline" onClick={reset} disabled={isLoading}>
          Reset
        </Button>
      </div>
    </form>
  );
}

function useUpdateVoiceActorForm() {
  const updateVoiceActor = useEditorStore(
    (state) => state.service.updateVoiceActor,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [voiceActorExternalId, setVoiceActorExternalId] = useState("");
  const [isGlobal, setIsGlobal] = useState(false);

  const isValid = useMemo(() => {
    return voiceActorExternalId.length > 0;
  }, [voiceActorExternalId]);

  const reset = useCallback(() => {
    setVoiceActorExternalId("");
    setIsGlobal(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!isValid || isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      await updateVoiceActor(voiceActorExternalId, isGlobal);
      toasts.info("Voice actor updated successfully");
      reset();
    } catch (error) {
      console.error(error);
      toasts.error("Failed to update voice actor");
    } finally {
      setIsLoading(false);
    }
  }, [
    voiceActorExternalId,
    isGlobal,
    isValid,
    isLoading,
    updateVoiceActor,
    reset,
  ]);

  return {
    voiceActorExternalId,
    isGlobal,
    setVoiceActorExternalId,
    setIsGlobal,
    isValid,
    handleSubmit,
    reset,
    isLoading,
  };
}

export default UpdateVoiceActor;
